<template>
  <div class="definition">
    <component
      :is="typeDefinition.component"
      :name="name"
      :type="typeDefinition.type"
      @edit="$emit('edit', index)"
      @delete="$emit('delete', index)"
    />
    <span
      v-if="hasDuplicateName"
      class="warning-icon-wrapper"
      title="Duplicate field name."
    >
      <TriangleWarningIcon class="warning-icon" />
    </span>
    <div class="column-3 system-tag-column">
      <span v-if="showSystemTag" class="system-tag">System</span>
      <span v-if="isTitleField" class="title-tag">Title</span>
      <span v-else-if="showLockedTag" class="locked-tag">Locked</span>
      <span v-if="isNotesField" class="notes-tag">Notes</span>
      <span v-if="isHidden" class="hidden-tag">Hidden</span>
      <BaseType
        v-if="relationTarget"
        class="relation-target"
        :title="relationTarget.data_name"
        :image="getTypeIcon(profile, relationTarget)"
        :color="getTypeColor(relationTarget)"
        size="s"
        @click="openRelationTarget"
      />
    </div>
    <div class="actions">
      <ButtonMenu
        v-if="menuItems.length > 0"
        :offset="[0, 4]"
        placement="bottom-start"
        :items="menuItems"
        @select="onMenuSelect"
      >
        <template #btn>
          <BaseButton kind="auxiliary">
            <template #icon>
              <MoreIcon />
            </template>
          </BaseButton>
        </template>
      </ButtonMenu>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, PropType, toRefs } from "vue";

import { GraphCustomType, GraphDatabase } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseType from "@/components/base/BaseType.vue";
import {
  getTypeColor,
  getTypeIcon,
  relationTargetTableId,
} from "@/components/databases";
import ButtonMenu, {
  type ButtonMenuItem,
} from "@/components/elements/ButtonMenu.vue";
import {
  contextDefinitionType,
  DefaultPropertyType,
  DefinitionType,
  extractOptionalFromCustom,
  isContextProperty,
  matchDefinition,
} from "@/components/graph/definition/graph-definition";
import UnknownDefinition from "@/components/graph/definition/UnknownDefinition.vue";
import { isNoteType, isTextField } from "@/components/graph/predicates";
import CrossedEyeIcon from "@/components/icons/CrossedEyeIcon.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import EditIcon from "@/components/icons/EditIcon.vue";
import EyeIcon from "@/components/icons/EyeIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import NotesIcon from "@/components/icons/NotesIcon.vue";
import TitleIcon from "@/components/icons/TitleIcon.vue";
import TriangleWarningIcon from "@/components/icons/TriangleWarningIcon.vue";
import * as inject from "@/components/inject";
import { getWorkspace } from "@/composables/useProfile";
import { navigateToType } from "@/utils/navigate";

type DisplayDefinition = Omit<DefinitionType, "type"> & {
  type: GraphCustomType | DefaultPropertyType;
};

export default defineComponent({
  name: "Definition",
  components: {
    ButtonMenu,
    MoreIcon,
    BaseButton,
    BaseType,
    TriangleWarningIcon,
  },
  props: {
    name: {
      type: String,
      required: false,
    },
    type: {
      type: Object as PropType<GraphCustomType | DefaultPropertyType>,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    index: { type: Number, required: true },
    hasDuplicateName: { type: Boolean, default: false },
    isTitleField: { type: Boolean, default: false },
    isNotesField: { type: Boolean, default: false },
    isHidden: { type: Boolean, default: false },
    // Whether the property belongs to an integration-managed database.
    isIntegration: { type: Boolean, default: false },
    showSystemTag: { type: Boolean, default: false },
    showLockedTag: { type: Boolean, default: false },
    noEdit: { type: Boolean, default: false },
  },
  emits: {
    delete: (_index: number) => true,
    edit: (_index: number) => true,
    setTitle: (_index: number) => true,
    setNotes: (_index: number) => true,
    toggleHidden: (_index: number) => true,
  },
  setup(props, { emit }) {
    const { name, type } = toRefs(props);
    const types = inject.types();
    const workspaceDatabases = inject.workspaceDatabases();
    const currentWorkspaceId = inject.currentWorkspaceId();
    const profile = inject.profile();
    const router = inject.router();

    const optionalDefinition = computed<DisplayDefinition | undefined>(() => {
      if (type.value) {
        const typeWithOptional = extractOptionalFromCustom(type.value);
        // The Context column has the same type as a URL property; it is
        // recognized by name.
        if (isContextProperty({ name: name.value, type: type.value }))
          return {
            ...contextDefinitionType,
            optional: !!typeWithOptional.optional,
          };
        const matchedDefinition = matchDefinition(typeWithOptional);
        if (!matchedDefinition) return undefined;
        const definition: DisplayDefinition = {
          ...matchedDefinition,
          type: typeWithOptional,
        };
        return definition;
      } else return undefined;
    });
    const typeDefinition = computed(() => {
      if (optionalDefinition.value) {
        return optionalDefinition.value;
      } else
        return {
          type: { type: "null" },
          text: "Unknown",
          component: UnknownDefinition,
          icon: EditIcon,
        };
    });
    // The type target of a relation property, shown as a chip next to the
    // tags. The relation target can be a table id (possibly an alias) or a
    // default kind ("task", "page", ...). Resolve it to a canonical table id,
    // then look it up in the current workspace's databases and, failing that,
    // in the personal databases (which include the default and system types).
    const relationTarget = computed<GraphDatabase | undefined>(() => {
      const definitionType =
        type.value && extractOptionalFromCustom(type.value);
      if (!definitionType || definitionType.type !== "relation")
        return undefined;
      const tableId = relationTargetTableId(definitionType.table);
      const wsId = currentWorkspaceId.value;
      return (
        (wsId ? workspaceDatabases.value?.[wsId]?.[tableId] : undefined) ??
        types.value?.[tableId]
      );
    });
    // Navigate to the target type's edition page. The target may live in
    // another workspace than the edited type (e.g. the personal workspace
    // for relations to built-in kinds), so the owning organization is
    // resolved from the profile. The route component is keyed on the path
    // (see `routeComponentKey`), so this works from TypeEdition itself.
    const openRelationTarget = (event: MouseEvent) => {
      const target = relationTarget.value;
      if (!target) return;
      const organization = getWorkspace(
        profile.value,
        target.workspace
      )?.organization;
      if (!organization) return;
      void navigateToType(
        router,
        organization.id,
        target.workspace,
        target.id,
        undefined,
        event
      );
    };

    const canBeTitle = computed(
      () =>
        !!type.value &&
        type.value.type !== "default" &&
        isTextField({ value: type.value })
    );
    const canBeNotes = computed(
      () =>
        !!type.value &&
        type.value.type !== "default" &&
        (isNoteType(type.value) || isTextField({ value: type.value }))
    );
    // "Set as title" / "Set as notes" only changes the table's display metadata,
    // not the columns themselves, so they remain available on integration-managed
    // (read-only schema) tables.
    const canShowSetTitle = computed(
      () => canBeTitle.value && !props.isTitleField
    );
    // Designating a notes column is only offered on integration databases:
    // custom types already model their body as a real Notes property.
    // "Unset as notes" stays available anywhere so an existing designation
    // can always be removed.
    const canShowSetNotes = computed(
      () => canBeNotes.value && (props.isIntegration || props.isNotesField)
    );

    const menuItems = computed<ButtonMenuItem[]>(() => [
      ...(!props.readonly && !props.noEdit
        ? [{ id: "edit", text: "Edit", icon: EditIcon }]
        : []),
      ...(canShowSetTitle.value
        ? [{ id: "setTitle", text: "Set as title", icon: TitleIcon }]
        : []),
      ...(canShowSetNotes.value
        ? [
            {
              id: "setNotes",
              text: props.isNotesField ? "Unset as notes" : "Set as notes",
              icon: NotesIcon,
            },
          ]
        : []),
      // The title property is always displayed, so it cannot be hidden.
      ...(!props.isTitleField
        ? [
            {
              id: "toggleHidden",
              text: props.isHidden ? "Show" : "Hide",
              icon: props.isHidden ? EyeIcon : CrossedEyeIcon,
              info: "Hidden properties are collapsed by default.",
            },
          ]
        : []),
      ...(!props.readonly
        ? [
            {
              id: "delete",
              text: "Delete",
              icon: DeleteIcon,
              tint: "red" as const,
            },
          ]
        : []),
    ]);

    const onMenuSelect = (id: string) => {
      switch (id) {
        case "edit":
          emit("edit", props.index);
          break;
        case "setTitle":
          emit("setTitle", props.index);
          break;
        case "setNotes":
          emit("setNotes", props.index);
          break;
        case "toggleHidden":
          emit("toggleHidden", props.index);
          break;
        case "delete":
          emit("delete", props.index);
          break;
      }
    };

    return {
      typeDefinition,
      relationTarget,
      openRelationTarget,
      getTypeColor,
      getTypeIcon,
      profile,
      canBeTitle,
      canBeNotes,
      canShowSetTitle,
      canShowSetNotes,
      menuItems,
      onMenuSelect,
    };
  },
});
</script>
<style scoped lang="scss">
.definition {
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;

  .warning-icon-wrapper {
    margin-inline-start: 8px;
    cursor: help;
    display: inline-flex;
    align-items: center;
  }

  .warning-icon {
    width: 16px;
    height: 16px;
    color: #f59e0b;
  }

  .system-tag-column {
    display: flex;
    align-items: center;
    gap: 6px;

    .system-tag,
    .locked-tag,
    .title-tag,
    .notes-tag,
    .hidden-tag {
      padding: 2px 8px;
      border-radius: $border-radius-m;
      @extend %font-small-regular;

      @include themed() {
        background-color: t($grey-05);
        color: t($grey-60);
      }
    }
  }

  .actions {
    display: flex;
  }
}
</style>
